import React, { useState } from 'react';
import { GraduationCap, Plane, FileCheck, Briefcase, Users, Globe, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const ServicesV2 = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: GraduationCap,
      title: "Study Abroad Consulting",
      shortDesc: "Expert guidance for international education",
      fullDesc: "Navigate the complex world of international education with confidence. We help you select the perfect university and course aligned with your career goals, ensuring you make informed decisions for your future.",
      features: [
        "University & course selection",
        "Admission application support",
        "Scholarship guidance",
        "Career counseling"
      ],
      stats: { number: "5,000+", label: "Students Placed" },
      color: "from-[#1565C0] to-[#0D47A1]",
      bgPattern: "bg-blue-50"
    },
    {
      icon: Plane,
      title: "Immigration Services",
      shortDesc: "Comprehensive immigration solutions",
      fullDesc: "Whether you're planning to migrate for work, family reunion, or permanent residency, our immigration experts provide end-to-end support to make your transition smooth and successful.",
      features: [
        "PR application assistance",
        "Family sponsorship",
        "Skilled migration pathways",
        "Post-arrival support"
      ],
      stats: { number: "2,500+", label: "Visas Approved" },
      color: "from-[#FF7043] to-[#F4511E]",
      bgPattern: "bg-orange-50"
    },
    {
      icon: FileCheck,
      title: "Student Visa Assistance",
      shortDesc: "Maximize your visa approval chances",
      fullDesc: "Student visa applications can be complex and stressful. Our specialists ensure your application is complete, accurate, and compelling, significantly increasing your chances of approval.",
      features: [
        "Document preparation",
        "Visa interview coaching",
        "Financial documentation",
        "Appeal assistance"
      ],
      stats: { number: "95%", label: "Success Rate" },
      color: "from-[#8BC34A] to-[#7CB342]",
      bgPattern: "bg-green-50"
    },
    {
      icon: Briefcase,
      title: "Work Permit & PR",
      shortDesc: "Build your future abroad",
      fullDesc: "Secure work permits and permanent residency with our expert assistance. We guide you through complex immigration processes, ensuring you meet all requirements for long-term success abroad.",
      features: [
        "Work permit applications",
        "PR eligibility assessment",
        "Points calculation",
        "Express entry support"
      ],
      stats: { number: "3,000+", label: "Work Visas" },
      color: "from-[#1565C0] to-[#8BC34A]",
      bgPattern: "bg-indigo-50"
    }
  ];

  const IconComponent = services[activeService].icon;

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 right-10 w-96 h-96 bg-[#1565C0] rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-10 w-80 h-80 bg-[#FF7043] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#8BC34A] to-[#7CB342] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              Our Expert Services
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive Solutions for Every Dream
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From initial consultation to settling abroad, we provide end-to-end support tailored to your unique journey.
          </p>
        </div>

        {/* Interactive Service Display */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto mb-16">
          {/* Left: Service Cards */}
          <div className="space-y-4">
            {services.map((service, index) => {
              const ServiceIcon = service.icon;
              const isActive = activeService === index;
              
              return (
                <Card
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`cursor-pointer transition-all duration-300 border-2 ${
                    isActive 
                      ? `border-[#1565C0] shadow-2xl scale-105 ${service.bgPattern}` 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-lg bg-white'
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${service.color} flex items-center justify-center flex-shrink-0 ${isActive ? 'scale-110' : ''} transition-transform duration-300`}>
                        <ServiceIcon size={28} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-xl font-bold mb-2 ${isActive ? 'text-[#1565C0]' : 'text-gray-900'}`}>
                          {service.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {service.shortDesc}
                        </p>
                      </div>
                      {isActive && (
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-[#1565C0]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Right: Detailed View */}
          <div className={`${services[activeService].bgPattern} rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden`}>
            {/* Background Icon */}
            <div className="absolute top-0 right-0 opacity-5">
              <IconComponent size={300} />
            </div>

            <div className="relative z-10">
              {/* Icon & Stats */}
              <div className="flex items-start justify-between mb-6">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${services[activeService].color} flex items-center justify-center`}>
                  <IconComponent size={40} className="text-white" />
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-gray-900">{services[activeService].stats.number}</div>
                  <div className="text-sm text-gray-600">{services[activeService].stats.label}</div>
                </div>
              </div>

              {/* Title & Description */}
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                {services[activeService].title}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {services[activeService].fullDesc}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-8">
                <div className="font-semibold text-gray-900 mb-4">What We Offer:</div>
                {services[activeService].features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${services[activeService].color} flex items-center justify-center flex-shrink-0`}>
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button 
                onClick={() => {
                  const element = document.getElementById('consultation');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`w-full bg-gradient-to-r ${services[activeService].color} text-white py-4 rounded-xl font-bold hover:shadow-xl transition-all duration-300 hover:scale-105`}
              >
                Get Started with {services[activeService].title}
              </button>
            </div>
          </div>
        </div>

        {/* Additional Services Highlight */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {[
            { icon: Users, label: "IELTS/PTE Coaching", color: "from-[#1565C0] to-[#0D47A1]" },
            { icon: Globe, label: "Travel Assistance", color: "from-[#FF7043] to-[#F4511E]" },
            { icon: Award, label: "Scholarship Guidance", color: "from-[#8BC34A] to-[#7CB342]" },
            { icon: TrendingUp, label: "Career Counseling", color: "from-[#1565C0] to-[#8BC34A]" }
          ].map((item, idx) => (
            <div key={idx} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center mx-auto mb-4`}>
                <item.icon size={28} className="text-white" />
              </div>
              <div className="font-semibold text-gray-900 text-sm">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesV2;
